<template>
  <StepPage
    :page="page"
    name="Slogan"
    :input="slogan"
    :loading="loading"
    @continue="saveSlogan"
  >
    <TextInput
      v-model="slogan"
      autofocus
      maxlength="40"
      data-hj-whitelist
      :status="sloganValidationStatus"
      :status-message="sloganValidationMessage"
      :label="render(page.input.label)"
      :placeholder="render(page.input.placeholder)"
      :prompt="page.input.prompt"
      :disabled="loading"
      input-styles="form-input-large"
      @enter="saveSlogan"
    />
  </StepPage>
</template>

<script>
import { mapState } from 'vuex'
import RenderVariables from '@/mixins/prismic/helpers/render-variables'
import { errorMessage } from '@/lib/helpers/error-messages'
import TextInput from '@/components/form/TextInput'
import { meta } from '@/mixins/content'
import { EventBus, Events } from '@/lib/events'
import StepPage from '@/components/StepPage'
import delay from '@/mixins/delay-page'
import {
  wordmark_validator,
  slogan_validator,
} from '@/lib/constants/validators'
import SloganContent from '@/content/slogan'

export default {
  transition: 'fade',

  layout: 'progress',

  components: { TextInput, StepPage },

  mixins: [RenderVariables, delay(), meta],

  data: () => ({
    submittingForm: false,
    slogan: '',
    wordmark: '',
    pendingAssetsPromise: Promise.resolve(),
    page: SloganContent,
  }),

  computed: {
    showSloganNameWarning() {
      return this.slogan.length > 20
    },
    sloganValidationMessage() {
      if (this.sloganValidationError.length > 0)
        return this.sloganValidationError
      if (this.showSloganNameWarning)
        return 'The best slogans are short and sweet.'
      return ''
    },
    sloganValidationError() {
      return errorMessage(this.$v.slogan, this.page?.input?.name)
    },

    sloganValidationStatus() {
      return this.sloganValidationError.length === 0 ? '' : 'error'
    },

    loading() {
      return this.submittingForm || !this.pageReady
    },

    ...mapState(['flags', 'session']),
  },

  validations: {
    slogan: slogan_validator,
    wordmark: wordmark_validator,
  },

  async mounted() {
    this.wordmark = this.session.wordmark || ''
    if (!this.wordmark) {
      this.redirectToBusinessName()
    }

    if (this.wordmark) {
      this.$v.wordmark.$touch()
      if (this.$v?.wordmark?.$error) this.redirectToBusinessName()
    }

    this.slogan = this.session.slogan || ''

    // Trigger validation when slogan is set through query params, or a returning customer
    if (this.slogan) {
      this.$v?.slogan?.$touch()
    }

    // Let's attempt to keyword right away
    await this.$store.dispatch('session/sendSessionToKeyworder')

    EventBus.$emit(Events.StepSetProgress, 30)
  },

  methods: {
    redirectToBusinessName() {
      this.$router.push({
        path: this.localePath('/business-name'),
        query: { ...this.$route.query },
      })
    },

    async saveSlogan() {
      if (this.$invalidInput() || this.submittingForm) {
        return
      }

      this.submittingForm = true
      const { slogan } = this
      try {
        if (slogan !== this.session.slogan) {
          this.$store.commit('session/setSlogan', slogan)
          this.$store.commit('editor/SET_UNVALIDATED_SLOGAN', slogan)
          this.$store.dispatch('app/refreshLogos')
        }

        let path = '/editor'

        if (this.flags.enable_enhanced_flow) {
          path = '/flow/industry'
        } else if (this.flags.skip_editor_ideas) {
          path = this.flags.editor_start_with_colors
            ? '/editor/colors'
            : '/editor/templates'
        } else if (this.flags.enable_standalone_ideas) {
          path = '/ideas'
        }
        this.$router.push(this.localePath(path))

        // Kick off more keywording here
        this.$store.dispatch('session/sendSessionToKeyworder')
      } catch {
        this.submittingForm = false
      }
    },
  },
}
</script>
